import {
  PRICES,
  PRICES_AND_BUMPS,
  TICKETS,
  TICKETS_AND_ADD_ONS,
  takesBumps,
  WHOLE_ORDER,
} from './scopePage';

export const EVERYWHERE_SENTENCE = 'Every checkout and event, whole order.';

const WHOLE_SENTENCE = {
  checkout: 'Applies to the whole order: every price and every bump.',
  event: 'Applies to the whole event: every ticket and every add-on.',
};
const NOUNS = {
  checkout: { item: 'price', bump: 'bump' },
  event: { item: 'ticket', bump: 'add-on' },
};

const plain = text => ({ text });
const bold = text => ({ text, bold: true });

const joinParts = (groups, separator) =>
  groups.flatMap((group, index) => (index === 0 ? group : [plain(separator), ...group]));

// A; A or B; A, B or C
const orList = parts =>
  parts.flatMap((part, index) => {
    if (index === 0) return [part];
    return [plain(index === parts.length - 1 ? ' or ' : ', '), part];
  });

const picksPhrase = (picks, noun, withDetail) => {
  if (picks.length === 0) return [plain(`any ${noun}`)];
  if (picks.length === 1 && withDetail && picks[0].detail) {
    return [bold(`${picks[0].name} (${picks[0].detail})`)];
  }
  return orList(picks.map(pick => bold(pick.name)));
};

const allItemsOf = rule => rule.items.length === 0;
const allBumpsOf = rule => rule.bumps.length === 0;

const condition = (rule, kind, withDetail) => {
  const nouns = NOUNS[kind];
  const items = picksPhrase(rule.items, nouns.item, withDetail);
  if (!takesBumps(rule.appliesTo)) return items;
  return [...items, plain(' and '), ...picksPhrase(rule.bumps, nouns.bump, withDetail)];
};

const discountSentence = rule => {
  const allItems = allItemsOf(rule);
  const allBumps = allBumpsOf(rule);
  const oneAndOne = !allItems && rule.items.length === 1 && !allBumps && rule.bumps.length === 1;
  const which = all => (all ? 'the' : 'those');

  switch (rule.appliesTo) {
    case PRICES:
      return 'Discounts that price only.';
    case TICKETS:
      return allItems ? 'Discounts the tickets only.' : 'Discounts those tickets only.';
    case PRICES_AND_BUMPS:
      return oneAndOne ? 'Discounts both.' : `Discounts the price and ${which(allBumps)} bumps.`;
    case TICKETS_AND_ADD_ONS:
      if (oneAndOne) return 'Discounts both.';
      if (!allItems && !allBumps) return 'Discounts those tickets and add-ons.';
      return `Discounts ${which(allItems)} tickets and ${which(allBumps)} add-ons.`;
    default:
      return '';
  }
};

const mergePlain = parts =>
  parts.reduce((merged, part) => {
    const last = merged[merged.length - 1];
    if (last && !last.bold && !part.bold) {
      merged[merged.length - 1] = plain(last.text + part.text);
      return merged;
    }
    merged.push(part);
    return merged;
  }, []);

/**
 * One page's rules as a sentence, in parts so names can be bold: `[{ text, bold? }]`.
 *
 * `kind` is 'checkout' or 'event'. Each rule is resolved to names:
 * `{ appliesTo, items: [{ name, detail }], bumps: [{ name, detail }] }`, the picked prices or
 * tickets and bumps or add-ons, an empty list meaning all of them. Rules on one page are
 * alternatives: their conditions join with ", or ", and only a lone rule shows a single pick's
 * price.
 */
export const describeCouponRules = ({
  kind,
  rules,
  appliesToSetupFee = false,
  appliesToTicketFees = false,
}) => {
  const parts = [];
  if (!rules.length || rules.some(rule => rule.appliesTo === WHOLE_ORDER)) {
    parts.push(plain(WHOLE_SENTENCE[kind]));
  } else {
    const withDetail = rules.length === 1;
    parts.push(
      plain('Applies when a customer buys '),
      ...joinParts(
        rules.map(rule => condition(rule, kind, withDetail)),
        ', or ',
      ),
      plain('. '),
      plain([...new Set(rules.map(discountSentence))].join(' ')),
    );
  }
  if (kind === 'checkout' && appliesToSetupFee) parts.push(plain(' Includes setup fees.'));
  if (kind === 'event' && appliesToTicketFees) parts.push(plain(' Includes ticket fees.'));
  return mergePlain(parts);
};

export const partsToText = (parts, { boldText = text => text } = {}) =>
  parts.map(part => (part.bold ? boldText(part.text) : part.text)).join('');

const pickIn = (options, ids) => options.filter(option => ids.includes(String(option.id)));

/**
 * A form rule (`{ appliesTo, itemIds, bumpIds }`) with names from the page's options, each
 * `{ id, name, detail }`. Picks no longer on the page are left out; none left reads as all.
 */
export const resolveCouponRule = (rule, { itemOptions = [], bumpOptions = [] }) => ({
  appliesTo: rule.appliesTo,
  items: pickIn(itemOptions, rule.itemIds ?? []),
  bumps: pickIn(bumpOptions, rule.bumpIds ?? []),
});
